import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {isStandalone, noop, Time, Util} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {ApiUi, UiSlot} from '@api/components'
import {Accounts} from '@client/accounts'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {TaskChatListSearch} from '@components/right-panel/task-chat-list/task-chat-list-search'
import {
  useCompanyId,
  useForceUpdate,
  useGlobalKeyDown,
  useNotificationSettings,
  useSyncEvent
} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {GroupChatProxy} from '@extensions/proxies/group-chat-proxy'
import {PersonalChatProxy} from '@extensions/proxies/personal-chat-proxy'
import {UserProxy} from '@extensions/proxies/user-proxy'
import {apiChatList} from '../../../api/components/api-chat-list'
import {cn} from '../../../containers/html-utils'
import {Checkbox} from '../../common/base/checkbox'
import {ChatList} from '../chat-list/chat-list'
import {PersonalChatListItem} from '../personal-chat-list/personal-chat-list-item'
import {ChatFilterKey, rightPanelStore} from '../right-panel'
import {getCanShowEmptyHint} from './ensure-cache-initialized'
import {getChats} from './get-chats'
import {GroupChatListItem} from './group-chat-list-item'
import MobileBannerImage from './mobile-banner.svg'
import MobileBannerImageEn from './mobile-banner-en.svg'
import {TaskChatListItem} from './task-chat-list-item'
import {Props, TaskChatItemProps} from './types'

export const ChatListItem = (
  props: GroupChatProxy | TaskChatItemProps | PersonalChatProxy
) => {
  if ('userId' in props) {
    return <PersonalChatListItem {...props} locationType={'right-panel'} />
  } else if ('users' in props) {
    return <GroupChatListItem {...props} locationType={'right-panel'} />
  } else {
    return <TaskChatListItem {...props} locationType={'right-panel'} />
  }
}

const ONLINE_HOURS_TARGET = 24
const NEW_USERS_SINCE_TIME = new Date('2024-12-5').getTime()
const HOURS_ONLINE_SINCE_REGISTRATION = 8

export const useOnlineTimer = () => {
  useEffect(() => {
    const interval = setInterval(() => {
      const timeOnline = localStorage.getItem('MOB-2183_timeOnline') ?? '0'
      localStorage.setItem('MOB-2183_timeOnline', (+timeOnline + 20).toString())
    }, 20 * 1000)
    return () => clearInterval(interval)
  }, [])
}

export const useBannerTimer = () => {
  useOnlineTimer()
  const [userProxy] = useState(() => UserProxy.create(Sync.getCurrentUser()))
  useEffect(() => {
    const wasForceClosed =
      localStorage.getItem('MOB-2183_mobileAppBannerForceClose') === 'true'
    if (wasForceClosed) {
      return noop
    }
    if (isStandalone() || Util.isDesktop()) {
      return noop
    }

    const timer = setInterval(() => {
      const isBannerVisible = getShouldShowMobileBanner()
      const isOnline = userProxy?.userStatus === 'online'
      if (isBannerVisible && isOnline) {
        const onlineTimeElapsedString: string =
          localStorage.getItem('MOB-2183_onlineTimeElapsed') ?? '0'
        const onlineTimeElapsed = +onlineTimeElapsedString
        localStorage.setItem(
          'MOB-2183_onlineTimeElapsed',
          (onlineTimeElapsed + 20).toString()
        )
      }
    }, 20 * 1000)
    return () => clearInterval(timer)
  }, [userProxy?.userStatus])
}

const getShouldShowMobileBanner = (): boolean => {
  if (isStandalone() || Util.isDesktop()) {
    return false
  }

  const hasFlagToForceOpen =
    localStorage.getItem('MOB-2183_mobileAppBannerForceOpen') === 'true'
  if (hasFlagToForceOpen) {
    return true
  }

  const wasForceClosed =
    localStorage.getItem('MOB-2183_mobileAppBannerForceClose') === 'true'
  if (wasForceClosed) {
    return false
  }

  const user = Accounts.get(globals.currentUserId)
  const lastMobileActivity = user.lastMobileActivity ?? 0
  const now = Date.now()

  const registrationDate = Sync.getCurrentUser().timestamp
  const isNewUser = registrationDate > NEW_USERS_SINCE_TIME
  if (!isNewUser) {
    return false
  }

  const timeOnline = localStorage.getItem('MOB-2183_timeOnline') ?? '0'
  const timeOnlineNumber = +timeOnline
  const isRegisteredForAtLeastTargetHours =
    timeOnlineNumber > HOURS_ONLINE_SINCE_REGISTRATION * 60 * 60
  if (!isRegisteredForAtLeastTargetHours) {
    return false
  }

  const didNotUseMobileApp = now - lastMobileActivity > 30 * 24 * 3600_000
  if (!didNotUseMobileApp) {
    return false
  }

  const onlineTimeElapsedString: string =
    localStorage.getItem('MOB-2183_onlineTimeElapsed') ?? '0'
  const onlineTimeElapsed = +onlineTimeElapsedString

  const shouldShow = onlineTimeElapsed < ONLINE_HOURS_TARGET * 60 * 60
  return shouldShow
}

export const TaskChatListContent = observer((props: Props) => {
  const {itemClick} = props
  useCompanyId()

  const forceUpdate = useForceUpdate()

  useSyncEvent('current', {debounceTime: 1000, filter: '*'}, forceUpdate, [])
  useSyncEvent('profile', {debounceTime: 1000, filter: '*'}, forceUpdate, [])

  useEffect(() => {
    apiChatList.triggerChatList = forceUpdate
  }, [])

  const box = Sync.getCurrentSandbox()

  const opened = rightPanelStore.state.taskChatList.openedBlock
  const toggleBlock = rightPanelStore.toggleTaskChatListOpenedBlock
  const closeBlock = rightPanelStore.closeTaskChatListOpenedBlock
  const isShared = !!SharedBoardsClient.getId()

  const companyHub = box.get(box.companyId) as Hub
  const isNewCompany =
    companyHub && companyHub.timestamp > Time.now() - 3 * 24 * 3600_000

  const bem = Util.bemFrom('task-chat-list')

  const filter = rightPanelStore.state.chatFilter
  const notificationSettings = useNotificationSettings()
  let chats = getChats(
    box,
    !SharedBoardsClient.getId() ? filter : {...filter, otherTasks: true},
    false,
    notificationSettings
  )

  if (apiChatList.filterChatList) {
    chats = chats.filter(apiChatList.filterChatList)
  }

  const shouldShowMobileBanner = getShouldShowMobileBanner()
  useEffect(() => {
    if (shouldShowMobileBanner) {
      void Tracking.send(TrackingEvent.chatPanelShowMobileAppBanner)
    }
  }, [shouldShowMobileBanner, rightPanelStore.state.chatPanel.opened])

  const canShowEmptyHint = getCanShowEmptyHint(box)
  const isEn = Lang.getLang() === 'en'
  const BannerComponent = isEn ? MobileBannerImageEn : MobileBannerImage

  const emptyHint = (
    <div className={bem('empty-hint')}>
      <div className={bem('empty-hint', 'title')}>
        {Lang.msg.onboardingNoTasks1}
      </div>
      <div className={bem('empty-hint', 'space')} />
      <div className={bem('empty-hint', 'text')}>
        {Lang.msg.onboardingNoTasks2}
        <br />
        {Lang.msg.onboardingNoTasks2_5}
      </div>
      <div className={bem('empty-hint', 'space')} />
      <div className={bem('empty-hint', 'text')}>
        {Lang.msg.onboardingNoTasks3} {Lang.msg.onboardingNoTasks4}{' '}
        {Lang.msg.onboardingNoTasks5}
      </div>
    </div>
  )

  const shouldShowChatsList =
    chats.length > 0 || !isNewCompany || !canShowEmptyHint

  return (
    <div
      className={bem('cnt', {
        withBanner: shouldShowMobileBanner
      })}
    >
      <div className={cn(bem('header-container'))}>
        <div className={bem('header')}>
          <div className={bem('title')}>{Lang.msg.chats2}</div>
          <div className={bem('actions')}>
            <UiSlot name="chat-list-header-actions-left" />
            <div
              className={bem('search-btn', {active: opened === 'search'})}
              onClick={toggleBlock('search')}
            >
              {Lang.msg.search}
            </div>
            {!isShared && (
              <div
                className={bem('settings-btn', {
                  active: opened === 'settings'
                })}
                onClick={toggleBlock('settings')}
              >
                {Lang.msg.chat.settings}
              </div>
            )}
            <UiSlot name="chat-list-header-actions-right" />
          </div>
        </div>

        <div>
          <UiSlot name="task-chat-list-header" />
          <ApiUi category="task-chat-list-header" id={'no' as DataObjectId} />
        </div>
      </div>

      {opened === 'settings' && (
        <ListSettings {...props} closeBlock={closeBlock('settings')} />
      )}

      {shouldShowChatsList && opened !== 'search' && (
        <>
          <ChatList
            chats={chats}
            itemClick={itemClick}
            chatView={ChatListItem}
          />
        </>
      )}

      {!shouldShowChatsList && canShowEmptyHint && emptyHint}

      {opened === 'search' && (
        <TaskChatListSearch onClose={closeBlock('search')} />
      )}

      {shouldShowMobileBanner && (
        <div className={bem('mobile-banner')}>
          <BannerComponent
            onClick={e => {
              const isClose = (e.target as HTMLElement).dataset.isClose
              if (isClose === 'true') {
                void Tracking.send(TrackingEvent.chatPanelCloseMobileAppBanner)
                localStorage.setItem(
                  'MOB-2183_mobileAppBannerForceClose',
                  'true'
                )
                forceUpdate()
              }
            }}
          />
        </div>
      )}
    </div>
  )
})

const ListSettings = observer((props: Props & {closeBlock: () => void}) => {
  const {closeBlock} = props
  const bem = Util.bemFrom('task-chat-list')
  useGlobalKeyDown({
    escape: () => {
      closeBlock()
      return true
    }
  })

  const toggleFilter =
    (type: ChatFilterKey, isEnabled = true) =>
    () => {
      if (isEnabled) {
        void Tracking.send(TrackingEvent.chatPanelChangeFilter)
        rightPanelStore.chatFilterToggle(type)
      }
    }
  const filter = rightPanelStore.state.chatFilter
  const isDisabled = apiChatList.areDefaultFiltersDisabled

  return (
    <div className={bem('settings')}>
      <div className={bem('settings-close')} onClick={closeBlock} />
      <div
        className={bem('settings-item')}
        onClick={toggleFilter('myTasks', !isDisabled)}
      >
        <Checkbox
          value={filter.myTasks}
          className={bem('settings-checkbox')}
          text={Lang.msg.myChatsFilter}
          disabled={isDisabled}
        />
      </div>
      <div
        className={bem('settings-item')}
        onClick={toggleFilter('otherTasks', !isDisabled)}
      >
        <Checkbox
          value={filter.otherTasks}
          className={bem('settings-checkbox')}
          text={Lang.msg.otherChatsFilter}
          disabled={isDisabled}
        />
      </div>
      <div
        className={bem('settings-item')}
        onClick={toggleFilter('personal', !isDisabled)}
      >
        <Checkbox
          value={filter.personal}
          className={bem('settings-checkbox')}
          text={Lang.msg.personalChatsFilter}
          disabled={isDisabled}
        />
      </div>
      <div
        className={bem('settings-item')}
        onClick={toggleFilter('groupChats', !isDisabled)}
      >
        <Checkbox
          value={filter.groupChats}
          className={bem('settings-checkbox')}
          text={Lang.msg.groupChats}
          disabled={isDisabled}
        />
      </div>
      <div
        className={bem('settings-item')}
        onClick={toggleFilter('privateTasks', !isDisabled)}
      >
        <Checkbox
          value={filter.privateTasks}
          className={bem('settings-checkbox')}
          text={Lang.msg.chat.privateTasks}
          disabled={isDisabled}
        />
      </div>
      <div className={bem('settings-line')} />
      <div
        className={bem('settings-item')}
        onClick={toggleFilter('starredOnTop')}
      >
        <Checkbox
          value={filter.starredOnTop}
          className={bem('settings-checkbox')}
          text={Lang.msg.chat.starredOnTop}
        />
      </div>
      <div
        className={bem('settings-item')}
        onClick={toggleFilter('unreadOnTop')}
      >
        <Checkbox
          value={filter.unreadOnTop}
          className={bem('settings-checkbox')}
          text={Lang.msg.chat.unreadOnTop}
        />
      </div>
    </div>
  )
})
